import React, { useEffect, useState, useCallback } from 'react';
import DashboardShell from '@/components/DashboardShell';
import api from '@/api/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import { Save, Plus, Trash2, Check } from 'lucide-react';
import { toast } from 'sonner';

const PAGES = [
  { key: 'landing', label: 'Landing' },
  { key: 'about', label: 'About' },
  { key: 'guide', label: 'Guide' },
  { key: 'services', label: 'Services' },
  { key: 'privacy', label: 'Privacy' },
  { key: 'terms', label: 'Terms' },
];

const humanize = (k) => k.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
const clone = (v) => JSON.parse(JSON.stringify(v));

function setDeep(obj, path, val) {
  const next = clone(obj);
  let cur = next;
  for (let i = 0; i < path.length - 1; i++) cur = cur[path[i]];
  cur[path[path.length - 1]] = val;
  return next;
}

function removeDeep(obj, path) {
  const next = clone(obj);
  let cur = next;
  for (let i = 0; i < path.length - 1; i++) cur = cur[path[i]];
  cur.splice(path[path.length - 1], 1);
  return next;
}

function emptyFrom(item) {
  if (typeof item === 'string') return '';
  if (Array.isArray(item)) return [];
  if (item && typeof item === 'object') {
    const o = {};
    for (const k in item) o[k] = emptyFrom(item[k]);
    return o;
  }
  return '';
}

function StringField({ label, path, value, onChange }) {
  const long = value.length > 60
    || ['text', 'body', 'desc', 'p1', 'p2', 'intro', 'heading', 'p'].some(k => label.toLowerCase() === k || label.toLowerCase().startsWith(k));
  return (
    <div className="mb-3">
      <label className="block text-[11px] font-medium uppercase tracking-wide mb-1" style={{ color: 'var(--text-muted)' }}>
        {humanize(label)}
      </label>
      {long ? (
        <Textarea value={value} onChange={e => onChange(path, e.target.value)} rows={3} className="text-sm" style={{ borderColor: 'var(--border-clr)' }} />
      ) : (
        <Input value={value} onChange={e => onChange(path, e.target.value)} className="text-sm h-9" style={{ borderColor: 'var(--border-clr)' }} />
      )}
    </div>
  );
}

function StringArrayField({ label, path, items, onChange, onRemove }) {
  return (
    <div className="mb-4">
      <p className="text-xs font-semibold uppercase tracking-wide mb-2" style={{ color: 'var(--text)' }}>{humanize(label)}</p>
      <div className="space-y-2">
        {items.map((item, i) => (
          <div key={i} className="flex gap-2 items-center">
            <Input value={item} onChange={e => onChange([...path, i], e.target.value)} className="text-sm h-9" style={{ borderColor: 'var(--border-clr)' }} />
            <Button type="button" variant="ghost" size="icon" className="h-8 w-8 flex-shrink-0" onClick={() => onRemove([...path, i])} style={{ color: 'var(--danger)' }}>
              <Trash2 size={13} />
            </Button>
          </div>
        ))}
      </div>
      <Button type="button" variant="outline" size="sm" className="mt-2" onClick={() => onChange(path, [...items, ''])} style={{ borderRadius: '8px', borderColor: 'var(--border-strong)' }}>
        <Plus size={13} className="mr-1.5" /> Add {humanize(label).replace(/s$/, '')}
      </Button>
    </div>
  );
}

function ObjectCard({ idx, label, cardPath, obj, onChange, onRemove }) {
  return (
    <div className="rounded-lg p-4 mb-2" style={{ border: '1px solid var(--border-clr)', backgroundColor: 'var(--bg-subtle)' }}>
      <div className="flex justify-between items-center mb-2">
        <span className="text-[11px] font-semibold uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>
          {humanize(label)} {idx + 1}
        </span>
        <Button type="button" variant="ghost" size="icon" className="h-7 w-7" onClick={() => onRemove(cardPath)} style={{ color: 'var(--danger)' }}>
          <Trash2 size={13} />
        </Button>
      </div>
      {Object.keys(obj).map(k => {
        const val = obj[k];
        const fieldPath = [...cardPath, k];
        if (typeof val === 'string') {
          return <StringField key={k} label={k} path={fieldPath} value={val} onChange={onChange} />;
        }
        if (Array.isArray(val) && (val.length === 0 || typeof val[0] === 'string')) {
          return <StringArrayField key={k} label={k} path={fieldPath} items={val} onChange={onChange} onRemove={onRemove} />;
        }
        return null;
      })}
    </div>
  );
}

function ObjectArrayField({ label, path, items, onChange, onRemove }) {
  return (
    <div className="mb-4">
      <p className="text-xs font-semibold uppercase tracking-wide mb-2" style={{ color: 'var(--text)' }}>{humanize(label)}</p>
      {items.map((item, i) => (
        <ObjectCard key={i} idx={i} label={label} cardPath={[...path, i]} obj={item} onChange={onChange} onRemove={onRemove} />
      ))}
      <Button type="button" variant="outline" size="sm" className="mt-2"
        onClick={() => onChange(path, [...items, emptyFrom(items[0] || {})])}
        style={{ borderRadius: '8px', borderColor: 'var(--border-strong)' }}>
        <Plus size={13} className="mr-1.5" /> Add {humanize(label).replace(/s$/, '')}
      </Button>
    </div>
  );
}

function SectionBlock({ sectionKey, value, onChange, onRemove }) {
  return (
    <div className="rounded-xl p-5 mb-5" style={{ border: '1px solid var(--border-clr)', backgroundColor: 'var(--surface)' }}>
      <p className="text-sm font-semibold mb-4" style={{ color: 'var(--text)' }}>{humanize(sectionKey)}</p>
      {Object.keys(value).map(k => {
        const val = value[k];
        const path = [sectionKey, k];
        if (typeof val === 'string') {
          return <StringField key={k} label={k} path={path} value={val} onChange={onChange} />;
        }
        if (Array.isArray(val)) {
          if (val.length === 0 || typeof val[0] === 'string') {
            return <StringArrayField key={k} label={k} path={path} items={val} onChange={onChange} onRemove={onRemove} />;
          }
          return <ObjectArrayField key={k} label={k} path={path} items={val} onChange={onChange} onRemove={onRemove} />;
        }
        return null;
      })}
    </div>
  );
}

// ── Services comparison matrix editor ─────────────────────────────
function ServicesEditor({ content, setContent }) {
  const pkgs = content.packages || [];
  const feats = content.features || [];

  const setPkg = (i, field, val) => setContent(prev => {
    const packages = prev.packages.map((p, idx) => idx === i ? { ...p, [field]: val } : p);
    return { ...prev, packages };
  });

  const setFeat = (i, field, val) => setContent(prev => {
    const features = prev.features.map((f, idx) => idx === i ? { ...f, [field]: val } : f);
    return { ...prev, features };
  });

  const setIncluded = (fi, pi, val) => setContent(prev => {
    const features = prev.features.map((f, idx) => {
      if (idx !== fi) return f;
      const included = [...(f.included || [])];
      included[pi] = val;
      return { ...f, included };
    });
    return { ...prev, features };
  });

  const addFeature = () => setContent(prev => ({
    ...prev,
    features: [...prev.features, { name: '', tooltip: '', included: pkgs.map(() => false) }],
  }));

  const removeFeature = (i) => setContent(prev => ({
    ...prev,
    features: prev.features.filter((_, idx) => idx !== i),
  }));

  return (
    <div className="space-y-6">
      {/* Packages */}
      <div className="rounded-xl p-5" style={{ border: '1px solid var(--border-clr)', backgroundColor: 'var(--surface)' }}>
        <p className="text-sm font-semibold mb-4" style={{ color: 'var(--text)' }}>Service Packages</p>
        <div className="space-y-3">
          {pkgs.map((pkg, i) => (
            <div key={i} className="grid grid-cols-2 gap-3 items-center">
              <div>
                <label className="block text-[11px] font-medium uppercase tracking-wide mb-1" style={{ color: 'var(--text-muted)' }}>Name</label>
                <Input value={pkg.name} onChange={e => setPkg(i, 'name', e.target.value)} className="text-sm h-9" style={{ borderColor: 'var(--border-clr)' }} data-testid={`pkg-name-${i}`} />
              </div>
              <div>
                <label className="block text-[11px] font-medium uppercase tracking-wide mb-1" style={{ color: 'var(--text-muted)' }}>Price</label>
                <Input value={pkg.price} onChange={e => setPkg(i, 'price', e.target.value)} className="text-sm h-9" style={{ borderColor: 'var(--border-clr)' }} data-testid={`pkg-price-${i}`} />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Features matrix */}
      <div className="rounded-xl p-5" style={{ border: '1px solid var(--border-clr)', backgroundColor: 'var(--surface)' }}>
        <p className="text-sm font-semibold mb-4" style={{ color: 'var(--text)' }}>Feature Matrix</p>

        {/* Column headers */}
        <div className="hidden sm:grid mb-3 text-[11px] font-semibold uppercase tracking-wide" style={{ color: 'var(--text-muted)', gridTemplateColumns: '2fr 2fr ' + pkgs.map(() => '1fr').join(' ') + ' 32px' }}>
          <span>Feature name</span>
          <span>Tooltip</span>
          {pkgs.map((p, i) => <span key={i} className="text-center">{p.name || `Pkg ${i + 1}`}</span>)}
          <span />
        </div>

        <div className="space-y-2">
          {feats.map((feat, fi) => (
            <div key={fi} className="grid gap-2 items-center p-2 rounded-lg" style={{ backgroundColor: 'var(--bg-subtle)', gridTemplateColumns: '2fr 2fr ' + pkgs.map(() => '1fr').join(' ') + ' 32px' }}>
              <Input value={feat.name} onChange={e => setFeat(fi, 'name', e.target.value)} placeholder="Feature name" className="text-sm h-8" style={{ borderColor: 'var(--border-clr)' }} data-testid={`feat-name-${fi}`} />
              <Input value={feat.tooltip || ''} onChange={e => setFeat(fi, 'tooltip', e.target.value)} placeholder="Tooltip text" className="text-sm h-8" style={{ borderColor: 'var(--border-clr)' }} />
              {pkgs.map((_, pi) => (
                <div key={pi} className="flex justify-center">
                  <button
                    type="button"
                    onClick={() => setIncluded(fi, pi, !(feat.included || [])[pi])}
                    className="w-7 h-7 rounded flex items-center justify-center transition-colors"
                    style={{ border: '1.5px solid var(--border-strong)', backgroundColor: (feat.included || [])[pi] ? 'var(--text)' : 'transparent' }}
                    data-testid={`feat-${fi}-pkg-${pi}`}
                    aria-pressed={(feat.included || [])[pi]}
                  >
                    {(feat.included || [])[pi] && <Check size={13} color="#fff" strokeWidth={2.5} />}
                  </button>
                </div>
              ))}
              <button type="button" onClick={() => removeFeature(fi)} className="h-7 w-7 flex items-center justify-center rounded hover:opacity-70" style={{ color: 'var(--danger)' }}>
                <Trash2 size={13} />
              </button>
            </div>
          ))}
        </div>

        <Button type="button" variant="outline" size="sm" className="mt-3" onClick={addFeature} style={{ borderRadius: '8px', borderColor: 'var(--border-strong)' }} data-testid="add-feature-btn">
          <Plus size={13} className="mr-1.5" /> Add feature row
        </Button>
      </div>
    </div>
  );
}

export default function SiteContent() {
  const [page, setPage] = useState('landing');
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback((p) => {
    setLoading(true);
    api.get(`/admin/site-content/${p}`)
      .then(r => setContent(r.data.content))
      .catch(() => toast.error('Failed to load'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(page); }, [page, load]);

  const onChange = (path, val) => setContent(prev => setDeep(prev, path, val));
  const onRemove = (path) => setContent(prev => removeDeep(prev, path));

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/site-content/${page}`, { content });
      toast.success('Content saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  return (
    <DashboardShell title="Site Content">
      <div className="max-w-3xl">
        <div className="flex flex-wrap gap-2 mb-6">
          {PAGES.map(p => (
            <button key={p.key} onClick={() => setPage(p.key)} data-testid={`content-tab-${p.key}`}
              className="px-4 py-2 text-xs font-semibold uppercase tracking-wide transition-colors"
              style={{
                backgroundColor: page === p.key ? 'var(--text)' : 'var(--surface)',
                color: page === p.key ? '#fff' : 'var(--text-muted)',
                border: '1px solid var(--border-clr)',
                borderRadius: '8px',
              }}>
              {p.label}
            </button>
          ))}
        </div>

        {loading || !content ? (
          <div className="space-y-3">
            {[1, 2, 3].map(i => <Skeleton key={i} className="h-24 w-full rounded-xl" />)}
          </div>
        ) : (
          <>
            <div className="flex justify-between items-center mb-4 sticky top-0 z-10 py-3" style={{ backgroundColor: 'var(--bg)' }}>
              <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
                Editing <strong style={{ color: 'var(--text)' }}>{PAGES.find(p => p.key === page)?.label}</strong> content
              </p>
              <Button onClick={save} disabled={saving} style={{ backgroundColor: 'var(--text)', color: '#fff', borderRadius: '8px' }} data-testid="save-content-button">
                <Save size={14} className="mr-1.5" /> {saving ? 'Saving…' : 'Save changes'}
              </Button>
            </div>
            {page === 'services' ? (
              <ServicesEditor content={content} setContent={setContent} />
            ) : (
              Object.keys(content).map(k => {
                const val = content[k];
                const path = [k];
                if (typeof val === 'string') {
                  return <StringField key={k} label={k} path={path} value={val} onChange={onChange} />;
                }
                if (Array.isArray(val)) {
                  if (val.length === 0 || typeof val[0] === 'string') {
                    return <StringArrayField key={k} label={k} path={path} items={val} onChange={onChange} onRemove={onRemove} />;
                  }
                  return <ObjectArrayField key={k} label={k} path={path} items={val} onChange={onChange} onRemove={onRemove} />;
                }
                if (val && typeof val === 'object') {
                  return <SectionBlock key={k} sectionKey={k} value={val} onChange={onChange} onRemove={onRemove} />;
                }
                return null;
              })
            )}
          </>
        )}
      </div>
    </DashboardShell>
  );
}
